import { useState, forwardRef, useImperativeHandle, useEffect } from 'react';
import { ASNForm } from './components/ASNForm';
import { InvoiceForm } from './components/InvoiceForm';
import { DocumentsList } from './components/DocumentsList';
import { TradingPartnerSelector } from './components/TradingPartnerSelector';
import { ConfigurationError } from './components/ConfigurationError';
import { AuthenticationError } from './components/AuthenticationError';
import { SessionBanner } from './components/SessionBanner';
import { useSessionStatus } from './hooks/useSessionStatus';
import { getSessionStatus } from './lib/session';
import type { TradingPartnerWithTemplate, InjectedTradingPartner } from './types/tradingPartner';
import type { ASNFormData } from './types/asn';
import type { InvoiceFormData } from './types/invoice';
import { updateASNDocument, updateInvoiceDocument, fetchASNDocumentById, fetchInvoiceDocumentById, createASNDocument, createInvoiceDocument } from './utils/documents';
import { eventBus } from './utils/eventBus';
import './utils/seedData';
import { fetchTradingPartnerWithTemplate, findTradingPartnerByName } from './utils/tradingPartners';
import { generateReferenceNumber } from './utils/generators';
import { applyTemplateToFormData } from './utils/templateApplicator';
import { applyInvoiceTemplate, createInvoiceLineItem } from './utils/invoiceTemplateApplicator';
import { overlayCustomerDefaults, getCachedCustomerDefaults } from './utils/customerDefaults';
import { getConfig, validateConfig, getCustomerId, type EDIConfig } from './lib/config';
import { initializeSupabase, supabase } from './lib/supabase';

type View = 'list' | 'detail';
type DocumentType = 'asn' | 'invoice';

export interface AppAPI {
  openASNEditor(data?: Partial<ASNFormData>, documentId?: string): void;
  openInvoiceEditor(data?: Partial<InvoiceFormData>, documentId?: string): void;
  updateDocumentStatus(documentId: string, status: string, type: 'asn' | 'invoice'): Promise<void>;
  closeEditor(): void;
  showDocumentList(): void;
}

interface AppProps {
  mode?: 'standalone' | 'embedded';
  tradingPartners?: InjectedTradingPartner[];
}

const SESSION_EXPIRED_MSG =
  'Your session has expired. Reload the page to create or edit messages; ' +
  'otherwise your work cannot be saved.';

const App = forwardRef<AppAPI, AppProps>(({ mode = 'standalone', tradingPartners: injectedTPs }, ref) => {
  // Configuration state
  const [isInitialized, setIsInitialized] = useState(false);
  const [configError, setConfigError] = useState<string | null>(null);
  const [authError, setAuthError] = useState<string | null>(null);
  const [sessionError, setSessionError] = useState<string | null>(null);
  const session = useSessionStatus();

  // Application state
  const [currentView, setCurrentView] = useState<View>('list');
  const [documentType, setDocumentType] = useState<DocumentType>('asn');
  const [selectedDocumentId, setSelectedDocumentId] = useState<string | undefined>();
  const [showTradingPartnerSelector, setShowTradingPartnerSelector] = useState(false);
  const [selectedTradingPartner, setSelectedTradingPartner] = useState<TradingPartnerWithTemplate | undefined>();
  const [externalData, setExternalData] = useState<Partial<ASNFormData | InvoiceFormData> | undefined>();
  const [documentError, setDocumentError] = useState<string | null>(null);
  /** True when the form was opened via Mode B (full document re-open). Controls "Edit … (Copy)" title. */
  const [isCopy, setIsCopy] = useState(false);

  // Initialize Supabase client on mount
  useEffect(() => {
    const initialize = async () => {
      try {
        // Get configuration from available sources
        const config = getConfig();
        
        if (!config) {
          setConfigError('No configuration found. Please provide Supabase URL and authentication credentials.');
          return;
        }
        
        // Validate configuration
        const validation = validateConfig(config);
        if (!validation.valid) {
          setConfigError(validation.error || 'Invalid configuration');
          return;
        }
        
        // Initialize Supabase client
        initializeSupabase(config);
        
        // Test connection with a simple query
        try {
          const { supabase } = await import('./lib/supabase');
          await supabase.from('trading_partners').select('count', { count: 'exact', head: true });
          console.log('✅ Successfully connected to Supabase');
        } catch (error: any) {
          console.error('❌ Failed to connect to Supabase:', error);
          if (error.message?.includes('JWT') || error.message?.includes('auth')) {
            setAuthError('Authentication failed. Your JWT token may be invalid or expired.');
          } else {
            setAuthError('Failed to connect to database. Please check your network connection.');
          }
          return;
        }
        
        setIsInitialized(true);
      } catch (error: any) {
        console.error('Initialization error:', error);
        setConfigError(error.message || 'Failed to initialize application');
      }
    };
    
    initialize();
  }, []);

  // Expose API methods via ref (must be before conditional returns)
  useImperativeHandle(ref, () => ({
    openASNEditor(data?: Partial<ASNFormData>, documentId?: string) {
      // If a specific document ID is given, open that document directly (existing doc edit).
      // Otherwise route through Mode A / Mode B detection.
      if (documentId) {
        handleEditDocument('asn', documentId, data);
      } else {
        handleOpenEditorWithData('asn', data);
      }
    },
    openInvoiceEditor(data?: Partial<InvoiceFormData>, documentId?: string) {
      if (documentId) {
        handleEditDocument('invoice', documentId, data);
      } else {
        handleOpenEditorWithData('invoice', data);
      }
    },
    async updateDocumentStatus(documentId: string, status: string, type: 'asn' | 'invoice'): Promise<void> {
      try {
        if (type === 'asn') {
          const doc = await updateASNDocument(documentId, {} as ASNFormData, status);
          eventBus.emit('document:status-updated', {
            type: 'asn',
            documentId,
            status,
          });
          console.log(doc);
        } else {
          const doc = await updateInvoiceDocument(documentId, {} as InvoiceFormData);
          eventBus.emit('document:status-updated', {
            type: 'invoice',
            documentId,
            status,
          });
          console.log(doc);
        }
      } catch (error) {
        console.error('Failed to update document status:', error);
        throw error;
      }
    },
    closeEditor() {
      setCurrentView('list');
      setSelectedDocumentId(undefined);
      setSelectedTradingPartner(undefined);
      setExternalData(undefined);
      setIsCopy(false);
    },
    showDocumentList() {
      setCurrentView('list');
      setSelectedDocumentId(undefined);
      setSelectedTradingPartner(undefined);
      setExternalData(undefined);
      setIsCopy(false);
    },
  }));

  // Retry initialization
  const handleRetry = () => {
    setConfigError(null);
    setAuthError(null);
    setIsInitialized(false);
    
    // Force reload to pick up new configuration
    window.location.reload();
  };

  /**
   * Returns true and **sets `sessionError` state** (triggering the blocking
   * card) if the session is already expired. Call at the top of any mutation
   * entry point — only from event handlers, never during render.
   */
  const blockedByExpiredSession = (): boolean => {
    if (getSessionStatus() === 'expired') {
      setSessionError(SESSION_EXPIRED_MSG);
      return true;
    }
    return false;
  };

  // ---------------------------------------------------------------------------
  // Mode Detection (per edi-edit-flow-spec.md)
  // ---------------------------------------------------------------------------

  /**
   * Determine which editor mode should be used based on the shape of the data
   * passed to openASNEditor / openInvoiceEditor.
   *
   *  'new'    – no data at all → show TP picker, empty form
   *  'mode-a' – has hint fields only → show TP picker, pre-fill hints after selection
   *  'mode-b' – has tradingPartner name + orders/details arrays → full document re-open
   */
  function detectEditorMode(data?: Partial<ASNFormData | InvoiceFormData>): 'new' | 'mode-a' | 'mode-b' {
    if (!data) return 'new';
    const hasTP = typeof data.tradingPartner === 'string' && data.tradingPartner.length > 0;
    const hasOrders = 'orders' in data && Array.isArray(data.orders);
    const hasDetails = 'details' in data && Array.isArray(data.details);
    if (hasTP && (hasOrders || hasDetails)) return 'mode-b';
    return 'mode-a';
  }

  /**
   * Strip non-form metadata fields from Mode B payload before persisting as form data.
   * `tradingPartner` and `type` are routing metadata; `_sourceBatchId` is kept for
   * audit trail (stored in the JSONB `data` column alongside normal form fields).
   */
  function stripModeBMetadata<T extends Record<string, any>>(data: T): Omit<T, 'tradingPartner' | 'type'> {
    const { tradingPartner: _tp, type: _t, ...rest } = data;
    return rest as Omit<T, 'tradingPartner' | 'type'>;
  }

  // ---------------------------------------------------------------------------
  // Mode B: create a new document from a full saved-document payload and
  // navigate directly to the form — skipping the TP picker.
  // ---------------------------------------------------------------------------

  const createAndOpenModeBDocument = async (
    type: DocumentType,
    partner: TradingPartnerWithTemplate,
    rawData: Partial<ASNFormData | InvoiceFormData> | undefined
  ) => {
    if (!partner.template?.id) {
      setDocumentError('Selected trading partner has no active template. Cannot open document.');
      return;
    }

    try {
      const cleanData = rawData ? stripModeBMetadata(rawData as Record<string, any>) : {};
      let newDoc;

      if (type === 'asn') {
        const today = new Date();
        const dateStr = today.toISOString().split('T')[0].replace(/-/g, '');
        const timeStr = today.toTimeString().substring(0, 5).replace(':', '');

        // Base blank ASN; override completely with the source document fields.
        const baseASN: ASNFormData = {
          purposeCode: '00',
          shipmentIdentification: generateReferenceNumber('ASN'),
          date: dateStr,
          time: timeStr,
          timeCode: 'CT',
          dates: [],
          grossWeight: '',
          packagingCode: 'PCS',
          ladingQuantity: '',
          carrierIdentificationQualifier: '2',
          carrierIdentificationCode: '',
          carrierReferenceNumber: '',
          transportationMethod: 'LT',
          routing: '',
          equipmentDescriptionCode: 'TL',
          equipmentInitial: '',
          equipmentNumber: '',
          shipmentMethodOfPayment: 'CA',
          scacCode: '',
          references: [],
          orders: [],
        };

        const finalData: ASNFormData = { ...baseASN, ...cleanData };

        newDoc = await createASNDocument(finalData, getCustomerId(), partner.template.id, partner.id, partner.name);
      } else {
        const today = new Date();
        const dateStr = today.toISOString().split('T')[0].replace(/-/g, '');

        const baseInvoice: InvoiceFormData = {
          purposeCode: '00',
          invoiceNumber: generateReferenceNumber('INV'),
          invoiceDate: dateStr,
          invoiceType: 'IN',
          purchaseOrderNumber: '',
          purchaseOrderDate: dateStr,
          currencyCode: 'USD',
          // WDEV-142 (Natalie I1): no fabricated Payment Terms default.
          paymentTerms: '',
          paymentTermsDescription: '',
          dates: [{ qualifier: '011', date: '' }],
          references: [{ qualifier: 'PK', id: '' }],
          names: [
            { type: 'BT', name: '', qualifier: '92', id: '' },
            { type: 'SE', name: '', qualifier: '92', id: '' },
            { type: 'SF', name: '', qualifier: '92', id: '', address1: '', city: '', state: '', postalCode: '', country: 'US' },
            { type: 'ST', name: '', qualifier: '92', id: '', address1: '', city: '', state: '', postalCode: '', country: 'US' },
          ],
          details: [],
          headerAllowancesCharges: [],
          headerTaxes: [],
          summary: {
            totalLineItems: 0,
            subtotal: '0.00',
            totalTax: '0.00',
            totalAllowances: '0.00',
            totalCharges: '0.00',
            invoiceTotal: '0.00',
          },
        };

        const finalData: InvoiceFormData = { ...baseInvoice, ...(cleanData as Partial<InvoiceFormData>) };

        newDoc = await createInvoiceDocument(finalData, getCustomerId(), partner.template.id, partner.id, partner.name);
      }

      eventBus.emit('document:created', {
        type,
        documentId: newDoc.id,
        data: newDoc.data,
      });

      setSelectedTradingPartner(partner);
      setSelectedDocumentId(newDoc.id);
      setExternalData(undefined);
      setIsCopy(true);
      setCurrentView('detail');
    } catch (error) {
      console.error('Failed to create Mode B document copy:', error);
      setDocumentError('Failed to open document for editing. Please try again.');
    }
  };

  // ---------------------------------------------------------------------------
  // Entry point for openASNEditor / openInvoiceEditor when no documentId is given.
  // Detects Mode A vs Mode B and routes accordingly.
  // ---------------------------------------------------------------------------

  const handleOpenEditorWithData = async (
    type: DocumentType,
    data?: Partial<ASNFormData | InvoiceFormData>
  ) => {
    if (blockedByExpiredSession()) return;
    setDocumentType(type);
    setDocumentError(null);

    const mode = detectEditorMode(data);

    if (mode === 'mode-b') {
      const tpName = data?.tradingPartner || '';
      // Keep the full payload so the TP-picker fallback can use it too.
      setExternalData(data);
      setIsCopy(true);

      try {
        const tp = await findTradingPartnerByName(tpName);
        if (tp?.template?.id) {
          // TP found — bypass TP picker entirely.
          await createAndOpenModeBDocument(type, tp, data);
        } else {
          // TP not found — show picker as fallback; full data stays in externalData.
          console.warn(`Mode B: trading partner "${tpName}" not found — falling back to TP picker.`);
          setShowTradingPartnerSelector(true);
        }
      } catch (error) {
        console.error('Mode B TP lookup failed, falling back to TP picker:', error);
        setShowTradingPartnerSelector(true);
      }
    } else {
      // Mode A (hints) or completely new document.
      setIsCopy(false);
      setExternalData(data);
      setSelectedDocumentId(undefined);
      setShowTradingPartnerSelector(true);
    }
  };

  const handleCreateNew = (type: DocumentType) => {
    if (blockedByExpiredSession()) return;
    setDocumentType(type);
    setIsCopy(false);
    setShowTradingPartnerSelector(true);
  };

  const handleTradingPartnerSelect = async (partner: TradingPartnerWithTemplate) => {
    if (blockedByExpiredSession()) return;
    setShowTradingPartnerSelector(false);

    // Validate that the partner has a valid template with an ID
    if (!partner.template?.id) {
      setDocumentError('Selected trading partner has no active template. Cannot create document.');
      return;
    }

    // Mode B fallback: TP was not found by name but user manually selected one.
    // Use the full document payload directly (skip template application).
    if (isCopy && externalData) {
      await createAndOpenModeBDocument(documentType, partner, externalData);
      return;
    }

    // Mode A / new document: apply template defaults then merge any hint fields.
    try {
      const today = new Date();
      const dateStr = today.toISOString().split('T')[0].replace(/-/g, '');
      const timeStr = today.toTimeString().substring(0, 5).replace(':', '');

      let newDoc;

      if (documentType === 'asn') {
        // Build default ASN form data with auto-generated IDs
        const initialASN: ASNFormData = {
          purposeCode: '00',
          shipmentIdentification: generateReferenceNumber('ASN'),
          date: dateStr,
          time: timeStr,
          timeCode: 'CT',
          dates: [],
          grossWeight: '',
          packagingCode: 'PCS',
          ladingQuantity: '',
          carrierIdentificationQualifier: '2',
          carrierIdentificationCode: '',
          carrierReferenceNumber: '',
          transportationMethod: 'LT',
          routing: '',
          equipmentDescriptionCode: 'TL',
          equipmentInitial: '',
          equipmentNumber: '',
          shipmentMethodOfPayment: 'CA',
          scacCode: '',
          references: [],
          orders: [],
        };

        // Apply template defaults if available, with the account's
        // customer_defaults profile overlaid (WDEV-142).
        const formData = partner.template?.template_config
          ? applyTemplateToFormData(
              initialASN,
              overlayCustomerDefaults(partner.template.template_config, getCachedCustomerDefaults(), 'asn', partner.id),
            )
          : initialASN;

        // Merge any external hint data + include trading partner name for traceability
        const finalData = externalData
          ? { ...formData, ...externalData, tradingPartner: partner.name }
          : { ...formData, tradingPartner: partner.name };

        // Persist to DB immediately with the correct trading partner template ID
        newDoc = await createASNDocument(
          finalData,
          getCustomerId(),
          partner.template.id,
          partner.id,
          partner.name
        );
      } else {
        // Build default Invoice form data with auto-generated IDs
        const initialInvoice: InvoiceFormData = {
          purposeCode: '00',
          invoiceNumber: generateReferenceNumber('INV'),
          invoiceDate: dateStr,
          invoiceType: 'IN',
          purchaseOrderNumber: '',
          purchaseOrderDate: dateStr,
          currencyCode: 'USD',
          // WDEV-142 (Natalie I1): no fabricated Payment Terms default.
          paymentTerms: '',
          paymentTermsDescription: '',
          dates: [{ qualifier: '011', date: '' }],
          references: [{ qualifier: 'PK', id: '' }],
          names: [
            { type: 'BT', name: '', qualifier: '92', id: '' },
            { type: 'SE', name: '', qualifier: '92', id: '' },
            { type: 'SF', name: '', qualifier: '92', id: '', address1: '', city: '', state: '', postalCode: '', country: 'US' },
            { type: 'ST', name: '', qualifier: '92', id: '', address1: '', city: '', state: '', postalCode: '', country: 'US' },
          ],
          details: [],
          headerAllowancesCharges: [],
          headerTaxes: [],
          summary: {
            totalLineItems: 0,
            subtotal: '0.00',
            totalTax: '0.00',
            totalAllowances: '0.00',
            totalCharges: '0.00',
            invoiceTotal: '0.00',
          },
        };

        // Apply the invoice template (previously skipped here) with the
        // account's customer_defaults profile overlaid, so header defaults and
        // Selling-Party / Ship-From identity (WDEV-142) seed on create — the
        // main create flow persists here, then opens the editor by id.
        const overlaidInvoiceConfig = partner.template?.template_config
          ? overlayCustomerDefaults(partner.template.template_config, getCachedCustomerDefaults(), 'invoice', partner.id)
          : undefined;
        let seededInvoice = overlaidInvoiceConfig
          ? applyInvoiceTemplate(initialInvoice, overlaidInvoiceConfig)
          : initialInvoice;

        // WDEV-142 (Natalie I4): auto-seed one editable line (with the template
        // UOM default) so a new invoice starts with a row, not an empty table.
        // This is the authoritative create path — it persists to the DB before
        // the form opens by id, so the seeding must happen here.
        if (seededInvoice.details.length === 0) {
          seededInvoice = { ...seededInvoice, details: [createInvoiceLineItem(1, overlaidInvoiceConfig)] };
        }

        // Merge any external hint data + include trading partner name for traceability
        const finalData = externalData
          ? { ...seededInvoice, ...externalData, tradingPartner: partner.name } as InvoiceFormData
          : { ...seededInvoice, tradingPartner: partner.name };

        // Persist to DB immediately with the correct trading partner template ID
        newDoc = await createInvoiceDocument(
          finalData,
          getCustomerId(),
          partner.template.id,
          partner.id,
          partner.name
        );
      }

      // Emit document:created event
      eventBus.emit('document:created', {
        type: documentType,
        documentId: newDoc.id,
        data: newDoc.data,
      });

      // Navigate to the form in EDIT mode with the persisted document ID
      setSelectedTradingPartner(partner);
      setSelectedDocumentId(newDoc.id);
      setExternalData(undefined);
      setCurrentView('detail');
    } catch (error) {
      console.error('Failed to create document:', error);
      setDocumentError('Failed to create document. Please try again.');
    }
  };

  const handleEditDocument = async (type: DocumentType, documentId?: string, data?: Partial<ASNFormData | InvoiceFormData>) => {
    if (blockedByExpiredSession()) return;
    setDocumentType(type);
    setDocumentError(null);
    
    // CASE 1: Editing EXISTING document
    if (documentId) {
      try {
        // Fetch document to get trading_partner_id
        const fetchFn = type === 'asn' ? fetchASNDocumentById : fetchInvoiceDocumentById;
        const doc = await fetchFn(documentId);
        
        if (!doc) {
          setDocumentError('Document not found');
          return;
        }
        
        if (!doc.trading_partner_id) {
          setDocumentError('This document has no Trading Partner assigned. Data is inconsistent. Please contact support.');
          return;
        }
        
        // Fetch the trading partner directly by its UUID (doc.trading_partner_id is now a TP FK)
        const { data: tpData, error: tpError } = await supabase
          .from('trading_partners')
          .select('*')
          .eq('id', doc.trading_partner_id)
          .maybeSingle();

        if (tpError || !tpData) {
          setDocumentError(`Trading Partner not found (ID: ${doc.trading_partner_id}). Error: ${tpError?.message || 'not in database'}`);
          return;
        }

        // Fetch the template if the document has one
        let template;
        if (doc.template_id) {
          const { data: tplData } = await supabase
            .from('trading_partner_templates')
            .select('*')
            .eq('id', doc.template_id)
            .maybeSingle();
          template = tplData || undefined;
        }

        const tp = { ...tpData, template } as TradingPartnerWithTemplate;
        
        setSelectedTradingPartner(tp);
        setSelectedDocumentId(documentId);
        setExternalData(data);
        setCurrentView('detail');
      } catch (error) {
        console.error('Error loading document:', error);
        setDocumentError('Failed to load document. Please try again.');
      }
    } 
    // CASE 2: Creating NEW document
    else {
      setSelectedDocumentId(undefined);
      setExternalData(data);
      setShowTradingPartnerSelector(true);
    }
  };

  const handleBackToList = () => {
    setCurrentView('list');
    setSelectedDocumentId(undefined);
    setSelectedTradingPartner(undefined);
    setExternalData(undefined);
    setIsCopy(false);
  };

  // Show error states
  if (configError) {
    return <ConfigurationError message={configError} onRetry={handleRetry} />;
  }

  if (authError) {
    return <AuthenticationError message={authError} onRetry={handleRetry} />;
  }

  if (!isInitialized) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Initializing application...</p>
        </div>
      </div>
    );
  }

  if (sessionError) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="flex-shrink-0 w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
              <svg className="w-6 h-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900">Session expired</h3>
          </div>
          <p className="text-gray-600 mb-6">{sessionError}</p>
          <button
            onClick={() => window.location.reload()}
            className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            Reload page
          </button>
        </div>
      </div>
    );
  }

  if (documentError) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="flex-shrink-0 w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
              <svg className="w-6 h-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Document Error</h3>
            </div>
          </div>
          <p className="text-gray-600 mb-6">{documentError}</p>
          <div className="flex gap-3">
            <button
              onClick={() => {
                setDocumentError(null);
                setCurrentView('list');
              }}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              Back to Documents
            </button>
            <button
              onClick={handleRetry}
              className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
            >
              Reload App
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <SessionBanner status={session.status} secondsRemaining={session.secondsRemaining} />
      {currentView === 'list' ? (
        <DocumentsList
          onCreateNew={handleCreateNew}
          onEditDocument={handleEditDocument}
          initialDocumentType={documentType}
          injectedTPs={injectedTPs}
        />
      ) : documentType === 'asn' ? (
        <ASNForm
          documentId={selectedDocumentId}
          tradingPartner={selectedTradingPartner}
          externalData={externalData as Partial<ASNFormData>}
          isCopy={isCopy}
          onBack={handleBackToList}
          sessionExpired={session.status === 'expired'}
        />
      ) : (
        <InvoiceForm
          documentId={selectedDocumentId}
          tradingPartner={selectedTradingPartner}
          externalData={externalData as Partial<InvoiceFormData>}
          isCopy={isCopy}
          onBack={handleBackToList}
          sessionExpired={session.status === 'expired'}
        />
      )}
      <TradingPartnerSelector
        isOpen={showTradingPartnerSelector}
        onClose={() => setShowTradingPartnerSelector(false)}
        onSelect={handleTradingPartnerSelect}
        documentType={documentType}
        injectedTPs={injectedTPs}
      />
    </>
  );
});

App.displayName = 'App';

export default App;
